/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Cart Drawer (recommendations)
Version:  1.6.0
Updated:  2026-10-13 — Recommendations in a full cart use the DW product card with image left, info right (#F3F3F3, no stars); the empty cart uses the normal card.
Source:   DW-Component-Library/Kits/Cart-Drawer

FUNCTION LOCKED. <dw-cart-recommendations> loads Shopify product
recommendations as DW product cards (sections/dw-cart-recommendations-fetch.liquid)
into its <dw-slider>, applies the block filters (sold out,
max price), and shows the block only when at least one product is left.
Products added to the cart leave the row on the next cart update.
Adding works through the card: one variant goes straight to the cart,
more variants open the DW quick add.
---------------------------------------------------------
*/
(function () {
  if (window.customElements.get('dw-cart-recommendations')) return;

  class DWCartRecommendations extends HTMLElement {
    connectedCallback() {
      /* The drawer keeps this element when the cart re-renders with the same first product:
         the new block wrapper starts hidden, so show it again when the row has products. */
      if (this._started) {
        const track = this.querySelector('[data-dw-slider-track]');
        this.setVisible(!!track && track.children.length > 0);
        return;
      }
      this._started = true;
      this._onCart = this.onCartUpdated.bind(this);
      document.addEventListener('dw:cart:updated', this._onCart);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('dw:cart:updated', this._onCart);
    }

    block() {
      return this.closest('.dw-cart__block');
    }

    setVisible(on) {
      const block = this.block();
      if (block) block.hidden = !on;
    }

    load() {
      const productId = this.dataset.productId;
      const base = this.dataset.url;
      const sectionId = this.dataset.fetchSectionId;
      if (!productId || !base || !sectionId) return;
      const limit = Math.max(1, parseInt(this.dataset.limit, 10) || 6);
      /* Ask for a few more, because products in the cart and filtered ones are left out. */
      const ask = Math.min(10, limit + 4);
      const url = base + '?product_id=' + encodeURIComponent(productId) + '&limit=' + ask + '&section_id=' + encodeURIComponent(sectionId) + '&intent=' + encodeURIComponent(this.dataset.intent || 'related');

      fetch(url, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html || '', 'text/html');
          const root = doc.querySelector('[data-dw-cart-recommendations-root]');
          let items = root ? Array.from(root.querySelectorAll('.dw-slider__item')) : [];
          items = items.filter((item) => this.keep(item)).slice(0, limit);
          this.render(items);
        })
        .catch(() => this.setVisible(false));
    }

    keep(item) {
      if (this.dataset.hideSoldOut === 'true' && item.dataset.available === 'false') return false;
      if (this.dataset.enableMaxPrice === 'true') {
        const max = Math.round(parseFloat(this.dataset.maxPrice || '0') * 100);
        if (max > 0 && parseInt(item.dataset.price, 10) > max) return false;
      }
      return true;
    }

    render(items) {
      const slider = this.querySelector('dw-slider');
      const track = this.querySelector('[data-dw-slider-track]');
      if (!track) return;
      track.innerHTML = '';
      items.forEach((item) => track.appendChild(document.importNode(item, true)));
      this.setVisible(items.length > 0);
      if (slider && typeof slider.refresh === 'function') slider.refresh();
    }

    /* A product that is now in the cart leaves the row; an empty row hides the block. */
    onCartUpdated(event) {
      const cart = event.detail && event.detail.cart;
      if (!cart || !cart.items) return;
      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      const track = this.querySelector('[data-dw-slider-track]');
      if (!track) return;
      track.querySelectorAll('.dw-slider__item').forEach((item) => {
        if (inCart.has(String(item.dataset.productId))) item.remove();
      });
      const left = track.querySelectorAll('.dw-slider__item').length;
      this.setVisible(left > 0);
      const slider = this.querySelector('dw-slider');
      if (slider && typeof slider.update === 'function') slider.update();
    }
  }

  window.customElements.define('dw-cart-recommendations', DWCartRecommendations);
})();
